import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content";

const SettingsContext = createContext(null);

const FALLBACK = { logo_url: null, content: {}, heading_font: "Cinzel", body_font: "Outfit" };
const CACHE_KEY = "tavern_settings";

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || null; } catch { return null; }
}

function useFonts(heading, body) {
  useEffect(() => {
    const families = [...new Set([heading, body])]
      .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700;800;900`)
      .join("&");
    let link = document.getElementById("tavern-fonts");
    if (!link) {
      link = document.createElement("link");
      link.id = "tavern-fonts";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    const root = document.documentElement.style;
    root.setProperty("--font-display", `'${heading}', serif`);
    root.setProperty("--font-body", `'${body}', sans-serif`);
  }, [heading, body]);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => ({ ...FALLBACK, ...(readCache() || {}) }));
  const [loading, setLoading] = useState(true);
  useFonts(settings.heading_font || "Cinzel", settings.body_font || "Outfit");

  const apply = (data) => {
    const next = { ...FALLBACK, ...data, content: data.content || {} };
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    setSettings(next);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      apply(data);
    } catch {
      /* keep cached */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const update = async (patch) => {
    const { data } = await api.put("/settings", patch);
    apply(data);
    return data;
  };

  const t = (key) => settings.content?.[key] || DEFAULT_CONTENT[key] || "";

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh, update, t }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be inside SettingsProvider");
  return ctx;
}

export function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (url.startsWith("/")) return `${base}${url}`;
  return url;
}
